import { memo, ReactNode, useEffect, useRef, useState } from 'react';

import { Entity, stringifyEntityRef } from '@backstage/catalog-model';

import Autocomplete from '@mui/material/Autocomplete';
import Backdrop from '@mui/material/Backdrop';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { useGetDaasEntities } from '../../../../hooks';
import { DAAS_PROJECT_TYPE } from '../../../../types';
import {
  ScaffolderFieldError,
  ScaffolderFormPaper,
  ScaffolderInputHeader,
} from '../../../components';
import { PROJECT_DESCRIPTION_INFO, PROJECT_NAME_INFO } from '../infoDoc';
import { useStyles } from '../styles';
import { DaasProjectInputData, ProjectBasicInfoValue } from '../types';
import { convertEntityToProject } from '../utils';

const DESCRIPTION_MAX_LENGTH = 250;

const findProjectEntity = (
  entities: Entity[],
  projectRef: string | undefined,
  projectName?: string,
): Entity | null => {
  if (projectRef) {
    return entities.find(e => stringifyEntityRef(e) === projectRef) ?? null;
  }
  if (projectName) {
    return entities.find(e => e.metadata.name === projectName) ?? null;
  }
  return null;
};

const toProjectData = (entity: Entity): DaasProjectInputData => ({
  ...(convertEntityToProject(entity) as DaasProjectInputData),
  projectRef: stringifyEntityRef(entity),
});

export interface EditBasicInfoSectionProps {
  value: ProjectBasicInfoValue;
  onChange: (value: ProjectBasicInfoValue) => void;
  onProjectEntitySelect: (data: DaasProjectInputData) => void;
  isFormAlreadyPopulated?: boolean;
  errors?: string[];
}

const EditBasicInfoSectionInner = ({
  value,
  onChange,
  onProjectEntitySelect,
  isFormAlreadyPopulated = false,
  errors = [],
}: EditBasicInfoSectionProps) => {
  const { classes } = useStyles();
  const { value: projectEntities = [], loading: projectsLoading } =
    useGetDaasEntities(DAAS_PROJECT_TYPE, false, true);

  const selectedEntity =
    value.projectName || value.projectRef
      ? findProjectEntity(projectEntities, value.projectRef, value.projectName)
      : null;

  const [inaccessibleProjectName, setInaccessibleProjectName] = useState<
    string | null
  >(null);
  const hasAutoPrefilled = useRef(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  // Stable refs — keeps the effect deps free of the callbacks without stale
  // closures. Always read .current inside callbacks instead of closing over the
  // prop directly.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const onProjectEntitySelectRef = useRef(onProjectEntitySelect);
  onProjectEntitySelectRef.current = onProjectEntitySelect;

  const projectNotAccessible =
    Boolean(inaccessibleProjectName) ||
    (errors.length > 0 && !value.projectName);

  useEffect(() => {
    if (projectNotAccessible) {
      sectionRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [projectNotAccessible]);

  // Auto-prefill when arriving via URL with a pre-set project name.
  // Skipped once the form has been populated (back-navigation or field edits)
  // to prevent overwriting user changes.
  useEffect(() => {
    if ((!value.projectName && !value.projectRef) || projectsLoading) return;
    const entity = findProjectEntity(
      projectEntities,
      value.projectRef,
      value.projectName,
    );
    if (!entity) {
      setInaccessibleProjectName(value.projectName);
      return;
    }
    setInaccessibleProjectName(null);
    if (hasAutoPrefilled.current || isFormAlreadyPopulated) return;
    hasAutoPrefilled.current = true;
    onProjectEntitySelectRef.current(toProjectData(entity));
  }, [
    value.projectName,
    value.projectRef,
    projectEntities,
    projectsLoading,
    isFormAlreadyPopulated,
  ]); // callbacks excluded via refs

  const handleEntitySelect = (_: unknown, entity: Entity) => {
    setInaccessibleProjectName(null);
    hasAutoPrefilled.current = true;
    onProjectEntitySelectRef.current(toProjectData(entity));
  };

  let projectHelperText: ReactNode;
  if (inaccessibleProjectName) {
    projectHelperText = (
      <ScaffolderFieldError
        message={`You do not have permission to edit project "${inaccessibleProjectName}". Select a different project.`}
      />
    );
  } else if (projectNotAccessible) {
    projectHelperText = <ScaffolderFieldError message="Project is required" />;
  }

  return (
    <Box>
      <ScaffolderFormPaper>
        <ScaffolderInputHeader
          title="Project configuration"
          description="Select a project to edit its configuration."
          variant="h5"
          divider
        />
        <Backdrop open={projectsLoading} className={classes.loadingProgress}>
          <CircularProgress size={48} />
        </Backdrop>
        <div ref={sectionRef}>
          <Box className={classes.inputContainer}>
            <ScaffolderInputHeader
              title="Project to edit"
              description="Select the project you want to edit."
              infoDoc={PROJECT_NAME_INFO}
            />
            <Autocomplete
              size="small"
              // `disableClearable` narrows the value type to a non-null Entity,
              // but nothing is selected until the user picks a project.
              value={selectedEntity as Entity}
              loading={projectsLoading}
              onChange={handleEntitySelect}
              options={projectEntities}
              disableClearable
              getOptionLabel={option =>
                option.metadata.title || option.metadata.name
              }
              isOptionEqualToValue={(option, selected) =>
                stringifyEntityRef(option) === stringifyEntityRef(selected)
              }
              renderInput={params => (
                <TextField
                  {...params}
                  label="Project"
                  variant="outlined"
                  margin="none"
                  required
                  error={projectNotAccessible}
                  helperText={projectHelperText}
                />
              )}
              renderOption={({ key, ...optionProps }, option) => (
                <li key={key} {...optionProps}>
                  <Box>
                    <Typography variant="body2" noWrap>
                      {option.metadata.title || option.metadata.name}
                    </Typography>
                    {option.metadata.title && (
                      <Typography
                        variant="caption"
                        noWrap
                        color="text.secondary"
                      >
                        {option.metadata.name}
                      </Typography>
                    )}
                  </Box>
                </li>
              )}
              noOptionsText={
                projectsLoading
                  ? 'Loading projects...'
                  : 'No accessible projects found'
              }
              loadingText="Loading projects..."
            />
          </Box>
        </div>
        <Box className={classes.inputContainer}>
          <ScaffolderInputHeader
            title="Description"
            description="Specify a description of the project."
            infoDoc={PROJECT_DESCRIPTION_INFO}
          />
          <TextField
            label="Description"
            value={value.description}
            onChange={e =>
              onChangeRef.current({ ...value, description: e.target.value })
            }
            variant="outlined"
            size="small"
            fullWidth
            multiline
            minRows={5}
            slotProps={{
              htmlInput: { maxLength: DESCRIPTION_MAX_LENGTH },
            }}
            helperText="Optional. Up to 250 characters."
          />
        </Box>
      </ScaffolderFormPaper>
    </Box>
  );
};

export const EditBasicInfoSection = memo(EditBasicInfoSectionInner);
